import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {FormFeedback, FormGroup, FormText, Input, Label} from 'reactstrap';
import useAlerts from 'src/hooks/use-alerts';
import {BillingBlock} from 'src/core/data/api/BillingBlock';
import ConfirmDialog from './confirm-dialog';
import {useReleaseAndExempt, useReleaseBlock} from './mutations';
import {useBillingBlocks} from './queries';
import {errorMessageId, isAlreadyExemptError} from './errors';
import {orNotAvailable} from './format';

export type ReleaseMode = 'release' | 'releaseAndExempt';

interface ReleaseDialogProps {
  block: BillingBlock | null;
  mode: ReleaseMode;
  onClose: () => void;
}

const ReleaseDialog = ({block, mode, onClose}: ReleaseDialogProps) => {
  const intl = useIntl();
  const {toast} = useAlerts();
  const release = useReleaseBlock();
  const releaseAndExempt = useReleaseAndExempt();
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const [target, setTarget] = useState<'device' | 'customer'>('device');
  const customerBlocks = useBillingBlocks(
      {status: 'ACTIVE', customerId: block?.customerId ?? null, deviceId: null},
      mode === 'releaseAndExempt' && !!block,
  );

  useEffect(() => {
    setNote('');
    setTouched(false);
    setTarget('device');
  }, [block, mode]);

  const busy = release.isLoading || releaseAndExempt.isLoading;
  const missingNote = note.trim() === '';
  const device = orNotAvailable(intl, block?.deviceName);
  const showError = (error: unknown) => toast.error(intl.formatMessage({id: errorMessageId(error)}));

  const confirm = () => {
    if (!block || missingNote) return;
    if (mode === 'release') {
      release.mutate({id: block.id, note}, {
        onSuccess: () => toast.success(intl.formatMessage({id: 'billingBlockReleased'})),
        onError: showError,
        onSettled: onClose,
      });
      return;
    }
    releaseAndExempt.mutate({block, note, target}, {
      onSuccess: (result) => {
        if (result.exempted) toast.success(intl.formatMessage({id: 'billingBlockReleasedAndExempted'}));
        else if ('released' in result) toast.warn(intl.formatMessage({id: 'billingBlockReleaseAllPartial'}, result));
        // Someone else exempted the same target meanwhile: the device is released and the target is exempt.
        else if (isAlreadyExemptError(result.error)) toast.warn(intl.formatMessage({id: errorMessageId(result.error)}));
        else toast.warn(intl.formatMessage({id: 'billingBlockReleasedNotExempted'}));
      },
      onError: showError,
      onSettled: onClose,
    });
  };

  const isRelease = mode === 'release';
  const description = isRelease ?
    intl.formatMessage({id: 'billingBlockReleaseBody'}, {device}) :
    intl.formatMessage({id: 'billingBlockReleaseAndExemptBody'}, {
      device,
      target: intl.formatMessage({id: target === 'device' ? 'billingBlockExemptTargetDevice' : 'billingBlockExemptTargetCustomer'}),
    });
  const noteErrorId = 'billing-block-release-note-error';

  return (
    <ConfirmDialog
      open={!!block}
      title={intl.formatMessage({id: isRelease ? 'billingBlockReleaseTitle' : 'billingBlockReleaseAndExempt'})}
      description={<p>{description}</p>}
      confirmLabel={intl.formatMessage({id: isRelease ? 'billingBlockRelease' : 'billingBlockReleaseAndExempt'})}
      confirmDisabled={missingNote}
      busy={busy}
      onConfirm={confirm}
      onCancel={onClose}
    >
      {!isRelease && (
        <FormGroup className="custom-formGroup" role="radiogroup" aria-labelledby="billing-block-exempt-target">
          <Label id="billing-block-exempt-target" tag="div" className="font-weight-bold">{intl.formatMessage({id: 'billingBlockExemptTargetLabel'})}</Label>
          {(['device', 'customer'] as const).map((option) => (
            <FormGroup check key={option} className="mb-2">
              <Input
                id={`billing-block-exempt-${option}`}
                type="radio"
                name="exemptTarget"
                value={option}
                checked={target === option}
                aria-describedby={`billing-block-exempt-${option}-help`}
                onChange={() => setTarget(option)}
              />
              <Label check for={`billing-block-exempt-${option}`}>
                {intl.formatMessage({id: option === 'device' ? 'billingBlockExemptTargetDeviceOption' : 'billingBlockExemptTargetCustomerOption'})}
              </Label>
              <FormText id={`billing-block-exempt-${option}-help`} className="d-block">
                {option === 'device' ?
                  intl.formatMessage({id: 'billingBlockExemptTargetDeviceHelp'}, {device}) :
                  intl.formatMessage(
                      {id: customerBlocks.data ? 'billingBlockExemptTargetCustomerHelp' : 'billingBlockExemptTargetCustomerHelpPending'},
                      {count: customerBlocks.data?.length ?? 0, customer: orNotAvailable(intl, block?.customerName)},
                  )}
              </FormText>
            </FormGroup>
          ))}
        </FormGroup>
      )}
      <FormGroup className="custom-formGroup">
        <Label for="billing-block-release-note">{intl.formatMessage({id: 'billingBlockReleaseNote'})}</Label>
        <Input
          id="billing-block-release-note"
          type="textarea"
          rows={3}
          required
          value={note}
          invalid={touched && missingNote}
          aria-describedby={touched && missingNote ? noteErrorId : undefined}
          onChange={(event) => setNote(event.target.value)}
          onBlur={() => setTouched(true)}
        />
        {touched && missingNote && (
          <FormFeedback id={noteErrorId}>{intl.formatMessage({id: 'billingBlockReleaseNoteRequired'})}</FormFeedback>
        )}
      </FormGroup>
    </ConfirmDialog>
  );
};

export default ReleaseDialog;
